import { rng } from './seed';

/**
 * The earth under the tree (§4.17, 2026-09-07 拍板): where a scene meets the
 * ground band it is no longer a straight cut. The bottom edge of the sky
 * carries a shallow strip of earth — a far bank that catches the last of the
 * horizon light along its ridge, and a near bank in the ground band's own
 * colour, so the band below reads as this same earth continuing and the only
 * line left is the land's own contour. The homepage's scene and a place page's
 * sky both draw it, from the same arithmetic, so the two grounds are one.
 *
 * It stays shallow on purpose: the 2026-09-05 ruling against a ground drawn in
 * the scene was about a 200-unit slab taking a quarter of a phone's first
 * screen. This is the band's top edge given a shape, some 4% of the scene.
 */

export interface Ground {
  /** The far bank, closed. Fill with the far-bank gradient. */
  far: string;
  /** The far bank's ridge, open — stroked with the rim light. */
  rim: string;
  /** The near earth, closed. Fill with the ground band's top colour. */
  near: string;
}

/* The two banks' colours, and the band they continue into, are the scene's (BanyanScene.css --banyan-bank-*, --banyan-earth, --banyan-ridge): the homepage's keep the hour. */

/** One rolling contour across [x0, x1], `rise` above the bottom `yb` on average, undulating by `amp`. */
function contour(x0: number, x1: number, yb: number, rise: number, amp: number, seed: number, phase: number): { closed: string; open: string } {
  const r = rng(seed);
  const n = 9;
  const pts: Array<[number, number]> = [];
  for (let i = 0; i <= n; i += 1) {
    const x = x0 + ((x1 - x0) * i) / n;
    const y = yb - rise + Math.sin(i * 1.7 + phase) * amp * 0.6 + (r() - 0.5) * amp;
    pts.push([x, y]);
  }
  let open = `M ${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`;
  for (let i = 1; i < pts.length; i += 1) {
    const [px, py] = pts[i - 1];
    const [x, y] = pts[i];
    const cx = ((px + x) / 2).toFixed(1);
    open += ` C ${cx} ${py.toFixed(1)}, ${cx} ${y.toFixed(1)}, ${x.toFixed(1)} ${y.toFixed(1)}`;
  }
  // Closed 20 below the bottom edge so no hairline of sky shows under it.
  const closed = `M ${x0.toFixed(1)} ${(yb + 20).toFixed(1)} L ${open.slice(2)} L ${x1.toFixed(1)} ${(yb + 20).toFixed(1)} Z`;
  return { closed, open };
}

/**
 * The ground across [x0, x1] with its surface at `yb` (the box's bottom edge).
 * `scale` is the unit the box is drawn in — 1 for the 600-unit scene or a
 * desktop's pixels, smaller on a phone's pixels — so the earth keeps its
 * proportion to the picture it stands under.
 */
export function ground(x0: number, x1: number, yb: number, scale: number, seed: number): Ground {
  const far = contour(x0, x1, yb, 24 * scale, 13 * scale, seed, 0.4);
  const near = contour(x0, x1, yb, 10 * scale, 6 * scale, seed + 7, 2.1);
  return { far: far.closed, rim: far.open, near: near.closed };
}
